/**
 * Centralized user-facing messages for AuthenticationDialog.
 * Single source of truth for all error and success copy per the auth dialog plan.
 *
 * Keep this module import-free: the server parity test
 * (server/__tests__/companyAccessBlock.parity.test.ts) imports it directly under node:test,
 * where the client's `@/` alias and client-only packages do not resolve.
 */

export const AuthMessages = {
  // Session and global
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  // Same text cloud sends with its 155, so old and new bundles read the same. [SC-409]
  AUTH_RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',

  // OAuth (Google)
  OAUTH_DOMAIN_BLOCKED:
    'Sign-in with Google is not allowed for this email domain. Use your work email or create an account with email and password.',
  OAUTH_ACCESS_DENIED: 'Google sign-in was cancelled. Click "Continue with Google" again to try.',
  OAUTH_GENERIC: 'Google sign-in failed. Please try again or use email and password.',
  OAUTH_NO_CODE: "Google didn't complete sign-in. Please try again.",
  OAUTH_CSRF: 'Sign-in link expired or invalid. Please try again.',
  OAUTH_NO_REDIRECT_URI: 'Your sign-in session expired. Please try again.',
  OAUTH_CALLBACK_SESSION: "We couldn't start your session. Please try again.",
  OAUTH_CALLBACK_TOKEN: 'Sign-in token was invalid. Please try again.',
  OAUTH_CALLBACK_NETWORK:
    'Connection problem during sign-in. Check your internet and try again.',
  OAUTH_GET_URL_NETWORK: "Can't reach sign-in service. Check your connection and try again.",
  OAUTH_GET_URL_DEFAULT: "We couldn't start Google sign-in. Please try again.",

  // Registration Mode Closed: the go-to-market waitlist (SC-443). The notice is the Create
  // Account tooltip and the main menu's Create Company page; both link to the waitlist.
  SIGNUP_CLOSED_NOTICE:
    "Sign-up for Sevv will be available soon. Join the waitlist and we'll let you know when it opens.",
  SIGNUP_WAITLIST_CTA: 'Join the waitlist',
  OPENS_IN_NEW_TAB: '(opens in a new tab)',
  OAUTH_SIGNUP_CLOSED_TITLE: 'Account not found',
  OAUTH_SIGNUP_CLOSED_BODY:
    "We couldn't find a Sevv account for this Google account. Sign-up for Sevv will be available soon. Join the waitlist and we'll let you know when it opens.",

  // Sign In – validation
  SIGNIN_EMAIL_INVALID: 'Enter a valid email address.',
  SIGNIN_PASSWORD_REQUIRED: 'Enter your password.',
  VALIDATION_SUMMARY: 'Please fix the errors below and try again.',

  // Sign In – server
  SIGNIN_PASSWORD_WRONG: 'Incorrect password. Try again or use "Forgot password?".',
  SIGNIN_ACCOUNT_NOT_FOUND:
    "We couldn't find an account with this email. Check the address or sign up.",
  SIGNIN_CREDENTIALS_INVALID:
    'Incorrect email or password. Check both and try again, or use "Forgot password?".',
  SIGNIN_DOMAIN_BLOCKED: "Sign-in is not allowed for this email domain.",
  SIGNIN_NETWORK: 'Connection problem. Check your internet and try again.',
  SIGNIN_SERVICE_UNAVAILABLE:
    'Sign-in service is temporarily unavailable. Please try again in a few minutes.',
  SIGNIN_OTHER: 'Sign-in failed. Please try again.',

  // Sign Up – validation
  SIGNUP_EMAIL_INVALID: 'Enter a valid email address.',
  SIGNUP_DOMAIN_RESTRICTED:
    "Registration isn't available for this email domain. Use a work email or contact your admin.",
  SIGNUP_EMAIL_VALIDATION_FAILED: "We couldn't check this email right now. Try again in a moment.",
  SIGNUP_PASSWORD_REQUIRED: 'Enter a password.',
  SIGNUP_PASSWORD_RULES: "Password doesn't meet the requirements below.",
  SIGNUP_CONFIRM_PASSWORD_REQUIRED: 'Enter your password again.',
  SIGNUP_PASSWORDS_MISMATCH: "Passwords don't match.",

  // Sign Up – server
  SIGNUP_EMAIL_EXISTS:
    'An account with this email already exists. Sign in above or use "Forgot password?".',
  // Sign-up and sign-in for an invited email that has never signed in. Same text cloud sends.
  EMAIL_INVITATION_PENDING:
    'This email has a pending invitation. Use the link in your invitation email to join, or ask your administrator to send a new one.',
  SIGNUP_EMAIL_GOOGLE_EXISTS:
    'This email is registered with Google Sign-In. Use Continue with Google below, or sign in with Google and set a password in your profile.',
  SIGNIN_USE_GOOGLE:
    'This account uses Google Sign-In. Use Continue with Google, or set a password in Profile → Security after signing in with Google.',
  OAUTH_ACCOUNT_CREATED:
    'Welcome! Your account was created with Google Sign-In.',
  OAUTH_PASSWORD_HINT:
    'Tip: You can add an email and password login anytime in Profile → Security.',
  SIGNUP_PASSWORD_REJECTED:
    "Password doesn't meet our security requirements. See the rules below.",

  // Sign Up – success
  SIGNUP_SUCCESS_VERIFICATION:
    'Account created. Check your email for a verification link or code.',
  SIGNUP_VERIFICATION_DELAYED:
    'Verification email may be delayed. Check your inbox in a few minutes.',

  // Forgot password (modal)
  FORGOT_PASSWORD_EMAIL_INVALID: 'Enter a valid email address.',
  FORGOT_PASSWORD_SUCCESS:
    "If an account exists with this email, we've sent a reset link. Check your inbox and spam folder.",
} as const

export type AuthMessageKey = keyof typeof AuthMessages

/** Get message by key; optional fallback for server/API messages when short and safe. */
export function getAuthMessage(key: AuthMessageKey, fallback?: string): string {
  return fallback && fallback.length > 0 && fallback.length < 120 ? fallback : AuthMessages[key]
}

/** The shape of the error state the dialog keeps for the field it belongs to. */
export interface AuthFieldError {
  field?: string
  message?: string
}

/**
 * Which message belongs under the sign-in password field, or null for none.
 *
 * The order is the point. A failed sign-in sets *both* the empty-field flag and the server
 * error — `clearAllErrors()` resets them, then the catch block sets both again — so reading
 * the empty-field flag first reported a rejected password as "Enter your password." while
 * the field visibly held one. The server's verdict is the more specific and more recent
 * fact, so it wins. [SC-208]
 */
export function signInPasswordMessage(args: {
  passwordEmpty: boolean
  error: AuthFieldError | null
}): string | null {
  if (args.error && args.error.field === 'password') {
    return args.error.message || AuthMessages.SIGNIN_PASSWORD_WRONG
  }
  if (args.passwordEmpty) return AuthMessages.SIGNIN_PASSWORD_REQUIRED
  return null
}

/**
 * Same ordering as {@link signInPasswordMessage}, with the local policy check sitting
 * between the server's verdict and silence: a password can be non-empty, fail the policy,
 * and never have reached the server.
 */
export function signUpPasswordMessage(args: {
  passwordEmpty: boolean
  policyErrors: readonly string[]
  error: AuthFieldError | null
}): string | null {
  if (args.error && args.error.field === 'password') {
    return args.error.message || AuthMessages.SIGNUP_PASSWORD_REJECTED
  }
  if (args.passwordEmpty) return AuthMessages.SIGNUP_PASSWORD_REQUIRED
  if (args.policyErrors.length > 0) return args.policyErrors.join(', ')
  return null
}

/** Sign-up's second password field: empty, or not the same as the first. '' when it's fine. */
export function confirmPasswordMessage(password: string, confirmPassword: string): string {
  if (!confirmPassword) return AuthMessages.SIGNUP_CONFIRM_PASSWORD_REQUIRED
  return confirmPassword === password ? '' : AuthMessages.SIGNUP_PASSWORDS_MISMATCH
}

// Parse error codes, as literals so this module stays free of the Parse SDK.
const PARSE_OBJECT_NOT_FOUND = 101 // wrong password, the Google-only hint, a pending invitation
const PARSE_OPERATION_FORBIDDEN = 119 // enhancedSignUp: email registered with Google
const PARSE_FUNCTION_NOT_FOUND = 141
const PARSE_EMAIL_TAKEN = 203 // also an invitee who has never signed in [SC-409]
const PARSE_EMAIL_NOT_FOUND = 205 // enhancedSignIn: no account with this email [SC-409]
const PARSE_REQUEST_LIMIT_EXCEEDED = 155 // enhancedSignIn/enhancedSignUp rate limiter [SC-409]

/**
 * Whether cloud is saying the email belongs to an invitee who has never signed in. Cloud reuses
 * 203 (sign-up) and 101 (sign-in) for this, so old bundles keep their generic copy, and the
 * message is the only thing that tells it apart. Matched loosely, like the "Google Sign-In"
 * hint, so a reworded sentence around the phrase still lands. [SC-409]
 */
export function isPendingInvitationMessage(message: unknown): boolean {
  return typeof message === 'string' && /pending\s+invitation/i.test(message)
}

/** How the dialog presents a failed sign-in. */
export interface SignInErrorView {
  message: string
  /** The input that shows the message inline. */
  inline?: 'email' | 'password'
  /** Replaces the service's `field` on the dialog's error state when set. */
  errorField?: string
  highlightGoogle: boolean
  toast: boolean
}

/**
 * Turn the service's sign-in error into what the dialog shows. The branch order is the order
 * the dialog has always used; the unknown-email branch sits ahead of the password ones because
 * an email with no account used to fall into "Incorrect password", pointing the user at the
 * wrong field. It keys on the code, not the field, so it still holds if the service's field
 * mapping changes. [SC-409]
 */
export function signInErrorView(authError: {
  code?: number
  message?: string
  field?: string
}): SignInErrorView {
  const quiet = { highlightGoogle: false, toast: false }
  const toastOnly = (message: string): SignInErrorView => ({
    message,
    highlightGoogle: false,
    toast: true,
  })

  // First, because a throttled attempt says nothing about the email or the password; putting
  // it on either field would send the user off to "fix" something that isn't wrong. [SC-409]
  if (authError.code === PARSE_REQUEST_LIMIT_EXCEEDED) {
    return { ...toastOnly(AuthMessages.AUTH_RATE_LIMITED), errorField: 'general' }
  }
  if (authError.field === 'domain') {
    return {
      ...quiet,
      message: getAuthMessage('SIGNIN_DOMAIN_BLOCKED', authError.message),
      inline: 'email',
      errorField: 'domain',
    }
  }
  if (authError.code === PARSE_EMAIL_NOT_FOUND) {
    return {
      ...quiet,
      message: AuthMessages.SIGNIN_ACCOUNT_NOT_FOUND,
      inline: 'email',
      errorField: 'email',
    }
  }
  // Ahead of the password branch, because the service maps every 101 to the password field.
  // An invitee who never set a password can't fix this by retyping it; the email is the fact
  // that matters. [SC-409]
  if (authError.code === PARSE_OBJECT_NOT_FOUND && isPendingInvitationMessage(authError.message)) {
    return {
      ...quiet,
      message: AuthMessages.EMAIL_INVITATION_PENDING,
      inline: 'email',
      errorField: 'email',
    }
  }
  if (authError.field === 'password') {
    return {
      ...quiet,
      message: AuthMessages.SIGNIN_PASSWORD_WRONG,
      inline: 'password',
      errorField: 'password',
    }
  }
  if (authError.code === PARSE_OBJECT_NOT_FOUND) {
    if (authError.field === 'google' || authError.message?.includes('Google Sign-In')) {
      return {
        message: AuthMessages.SIGNIN_USE_GOOGLE,
        errorField: 'google',
        highlightGoogle: true,
        toast: true,
      }
    }
    return {
      ...quiet,
      message: AuthMessages.SIGNIN_CREDENTIALS_INVALID,
      inline: 'password',
      errorField: 'password',
    }
  }
  if (
    authError.message?.includes('network') ||
    authError.message?.includes('connection') ||
    authError.message?.includes('timeout')
  ) {
    return toastOnly(AuthMessages.SIGNIN_NETWORK)
  }
  if (
    authError.code === PARSE_FUNCTION_NOT_FOUND ||
    authError.message?.toLowerCase().includes('unavailable')
  ) {
    return toastOnly(AuthMessages.SIGNIN_SERVICE_UNAVAILABLE)
  }
  return toastOnly(AuthMessages.SIGNIN_OTHER)
}

/** How the dialog presents a failed sign-up. */
export interface SignUpErrorView {
  message: string
  /** The input that shows the error state; only the email input also shows the message. */
  inline?: 'email' | 'password'
  /** Replaces the service's `field` on the dialog's error state when set. */
  errorField?: string
  highlightGoogle: boolean
  toast: boolean
}

/**
 * Turn the service's sign-up error into what the dialog shows. Kept out of the component, like
 * {@link signInErrorView}, so the branch order can be tested without rendering the dialog. The
 * order is the one the dialog has always used, with the rate-limit branch first: a throttled
 * sign-up says nothing about the fields, and must never read as the generic failure. [SC-409]
 */
export function signUpErrorView(authError: {
  code?: number
  message?: string
  field?: string
}): SignUpErrorView {
  const quiet = { highlightGoogle: false, toast: false }
  const toastOnly = (message: string): SignUpErrorView => ({
    message,
    highlightGoogle: false,
    toast: true,
  })

  if (authError.code === PARSE_REQUEST_LIMIT_EXCEEDED) {
    return { ...toastOnly(AuthMessages.AUTH_RATE_LIMITED), errorField: 'general' }
  }
  if (
    authError.field === 'google' ||
    (authError.code === PARSE_OPERATION_FORBIDDEN && authError.message?.includes('Google Sign-In'))
  ) {
    return {
      message: AuthMessages.SIGNUP_EMAIL_GOOGLE_EXISTS,
      inline: 'email',
      errorField: 'google',
      highlightGoogle: true,
      toast: false,
    }
  }
  // Ahead of the generic 203: "already exists, sign in or reset your password" sends an
  // invitee to a password they never had. [SC-409]
  if (authError.code === PARSE_EMAIL_TAKEN && isPendingInvitationMessage(authError.message)) {
    return {
      ...quiet,
      message: AuthMessages.EMAIL_INVITATION_PENDING,
      inline: 'email',
      errorField: 'email',
    }
  }
  if (authError.code === PARSE_EMAIL_TAKEN || authError.field === 'email') {
    return {
      ...quiet,
      message: AuthMessages.SIGNUP_EMAIL_EXISTS,
      inline: 'email',
      errorField: 'email',
    }
  }
  // The username is the email (SC-438), so a taken username is an existing account.
  if (authError.field === 'username') {
    return {
      ...quiet,
      message: AuthMessages.SIGNUP_EMAIL_EXISTS,
      inline: 'email',
      errorField: 'email',
    }
  }
  if (authError.field === 'password') {
    return {
      ...quiet,
      message: getAuthMessage('SIGNUP_PASSWORD_REJECTED', authError.message),
      inline: 'password',
      errorField: 'password',
    }
  }
  if (authError.field === 'domain') {
    return {
      ...quiet,
      message: getAuthMessage('SIGNIN_DOMAIN_BLOCKED', authError.message),
      inline: 'email',
      errorField: 'domain',
    }
  }
  if (
    authError.message?.includes('network') ||
    authError.message?.includes('connection') ||
    authError.message?.includes('timeout')
  ) {
    return toastOnly(AuthMessages.SIGNIN_NETWORK)
  }
  if (
    authError.code === PARSE_FUNCTION_NOT_FOUND ||
    authError.message?.toLowerCase().includes('unavailable')
  ) {
    return toastOnly(AuthMessages.SIGNIN_SERVICE_UNAVAILABLE)
  }
  return toastOnly(AuthMessages.SIGNIN_OTHER)
}

/**
 * Where the Google callback (App.tsx) leaves a refused sign-up for the dialog, which reads it
 * after the redirect back to '/'.
 */
export const OAUTH_SIGNUP_CLOSED_STORAGE_KEY = 'oauth_signup_closed'

/**
 * The start of cloud's refusal while Registration Mode is Closed: during the go-to-market
 * waitlist nobody may start a new account of their own, unless their email domain joins them
 * to an existing company. Cloud sends the same sentence for Google and email sign-up. [SC-443]
 */
const SIGNUP_CLOSED_MESSAGE_START = 'Sign-up for Sevv is not open yet'

export function isSignupClosedMessage(message: unknown): boolean {
  return typeof message === 'string' && message.includes(SIGNUP_CLOSED_MESSAGE_START)
}

/** The sessionStorage entry a failed Google sign-in callback leaves for the dialog. */
export type GoogleCallbackErrorEntry =
  | { key: typeof OAUTH_SIGNUP_CLOSED_STORAGE_KEY; value: { timestamp: number } }
  | {
      key: 'oauth_domain_error'
      value: { message: string; field: 'domain'; timestamp: number }
    }
  | {
      key: 'oauth_error'
      value: { message: string; field: 'google'; code?: number; timestamp: number }
    }

/**
 * Which sessionStorage entry a failed Google sign-in callback leaves, and what it holds. The
 * timestamp lets the dialog drop entries older than five minutes.
 */
export function googleCallbackErrorEntry(
  err: { code?: number; message?: string } | null | undefined,
  now: number
): GoogleCallbackErrorEntry {
  const message = err?.message
  // Ahead of the domain branch: closed sign-up is a waitlist, not a wrong email, so it gets a
  // popup rather than an error under the email field the person never typed into. [SC-443]
  if (isSignupClosedMessage(message)) {
    return { key: OAUTH_SIGNUP_CLOSED_STORAGE_KEY, value: { timestamp: now } }
  }
  if (
    err?.code === PARSE_OPERATION_FORBIDDEN &&
    (message?.includes('Registration blocked') ||
      message?.includes('Registrations are closed') ||
      message?.includes('Access blocked'))
  ) {
    return { key: 'oauth_domain_error', value: { message, field: 'domain', timestamp: now } }
  }
  const lower = (message || '').toLowerCase()
  let shown: string
  if (lower.includes('session')) {
    shown = AuthMessages.OAUTH_CALLBACK_SESSION
  } else if (lower.includes('token')) {
    shown = AuthMessages.OAUTH_CALLBACK_TOKEN
  } else if (lower.includes('network') || lower.includes('fetch')) {
    shown = AuthMessages.OAUTH_CALLBACK_NETWORK
  } else {
    shown = message || AuthMessages.OAUTH_GENERIC
  }
  return {
    key: 'oauth_error',
    value: { message: shown, field: 'google', code: err?.code, timestamp: now },
  }
}
